import { useEffect, useMemo, useRef, useState } from 'react';
import {
  emptyProductPresentation,
  parseProductPresentation,
  type ProductIntroBlock,
  type ProductPresentation,
} from '@travel/contracts';
import { ApiError, type AdminApiClient, type JsonRecord } from './api-client';
import './product-presentation-editor.css';

const record = (value: unknown): JsonRecord =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
type StopDecoration = { name: string; tags: string[]; images: { url: string; caption: string }[] };
function readStops(value: unknown): StopDecoration[] {
  const stops = record(value).stops;
  return Array.isArray(stops)
    ? (stops.filter((v) => typeof v?.name === 'string') as StopDecoration[])
    : [];
}

export function ProductPresentationEditor({
  api,
  appId,
  product,
  binding,
  loading,
  onDone,
  onDirtyChange,
}: {
  api: AdminApiClient;
  appId: string;
  product: JsonRecord;
  binding: JsonRecord;
  loading: boolean;
  onDone: () => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const original = record(binding.detailContentOverride);
  const [presentation, setPresentation] = useState<ProductPresentation>(emptyProductPresentation);
  const [decorations, setDecorations] = useState<StopDecoration[]>([]);
  const [gallery, setGallery] = useState<string[]>([]);
  const [baseline, setBaseline] = useState('');
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [failed, setFailed] = useState(false);
  const running = useRef(false);
  const [mediaAppKey, setMediaAppKey] = useState('');
  useEffect(() => {
    let cancelled = false;
    setMediaAppKey('');
    void api
      .get<JsonRecord>(`/api/admin/m2/apps/${encodeURIComponent(appId)}`)
      .then((app) => {
        const key = record(app.app).appKey;
        if (!cancelled) setMediaAppKey(typeof key === 'string' ? key : '');
      })
      .catch(() => {
        /* Image labels remain usable without app-view permission. */
      });
    return () => {
      cancelled = true;
    };
  }, [api, appId]);
  const thumbnail = (url: string) =>
    url.startsWith('/api/customer/public/m2/media/')
      ? mediaAppKey
        ? `${import.meta.env.VITE_API_BASE_URL ?? ''}${url}?appContext=${encodeURIComponent(mediaAppKey)}`
        : ''
      : url;
  const initial = JSON.stringify(original);
  useEffect(() => {
    const data = JSON.parse(initial) as JsonRecord;
    let next = emptyProductPresentation();
    try {
      if (data.presentation) next = parseProductPresentation(data.presentation);
    } catch {
      /* Existing malformed content is corrected by the operator. */
    }
    const stops = readStops(data.itinerary);
    const galleryUrls = Array.isArray(data.galleryImageUrls)
      ? data.galleryImageUrls.filter((v): v is string => typeof v === 'string')
      : [];
    setPresentation(next);
    setDecorations(stops);
    setGallery(galleryUrls);
    setBaseline(JSON.stringify({ presentation: next, decorations: stops, gallery: galleryUrls }));
  }, [initial, appId]);
  const dirty =
    Boolean(baseline) && baseline !== JSON.stringify({ presentation, decorations, gallery });
  useEffect(() => {
    onDirtyChange(dirty || busy);
    return () => onDirtyChange(false);
  }, [dirty, busy, onDirtyChange]);
  const stops = Array.isArray(product.routeStops) ? product.routeStops.map(record) : [];
  const media = useMemo(() => {
    const values = Array.isArray(product.media)
      ? product.media.map(record).filter((m) => m.status === 'ACTIVE' && m.mediaType === 'IMAGE')
      : [];
    return values.map((m, i) => ({ url: String(m.url), name: `图片 ${i + 1}` }));
  }, [product.media]);
  const imageOptions = [...media];
  for (const url of [
    ...gallery,
    ...decorations.flatMap((s) => (s.images ?? []).map((i) => i.url)),
    ...presentation.introduction.flatMap((b) => (b.type === 'image' ? [b.url] : [])),
  ]) {
    if (url && !imageOptions.some((m) => m.url === url))
      imageOptions.push({ url, name: `已选图片 ${imageOptions.length + 1}` });
  }
  const set = <K extends keyof ProductPresentation>(key: K, value: ProductPresentation[K]) =>
    setPresentation((p) => ({ ...p, [key]: value }));
  const updateBlock = (id: string, patch: Partial<ProductIntroBlock>) =>
    set(
      'introduction',
      presentation.introduction.map((b) =>
        b.id === id ? ({ ...b, ...patch } as ProductIntroBlock) : b,
      ),
    );
  function moveBlock(index: number, delta: number) {
    const blocks = [...presentation.introduction];
    [blocks[index], blocks[index + delta]] = [blocks[index + delta]!, blocks[index]!];
    set('introduction', blocks);
  }
  function addBlock(type: ProductIntroBlock['type'], index = presentation.introduction.length) {
    const id = `content-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const block: ProductIntroBlock =
      type === 'image' ? { id, type, url: '', caption: '' } : { id, type, text: '' };
    const blocks = [...presentation.introduction];
    blocks.splice(index, 0, block);
    set('introduction', blocks);
  }
  function updateStop(name: string, patch: Partial<StopDecoration>) {
    const current = decorations.find((s) => s.name === name) ?? { name, tags: [], images: [] };
    setDecorations([...decorations.filter((s) => s.name !== name), { ...current, ...patch }]);
  }
  async function save() {
    if (running.current) return;
    setMessage('');
    setFailed(false);
    let valid: ProductPresentation;
    try {
      valid = parseProductPresentation(presentation);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '请检查配置');
      setFailed(true);
      return;
    }
    running.current = true;
    setBusy(true);
    try {
      await api.send(
        `/api/admin/m2/apps/${encodeURIComponent(appId)}/products/${encodeURIComponent(String(product.id))}/override`,
        'PUT',
        {
          requireExisting: true,
          ...(binding.updatedAt ? { expectedUpdatedAt: binding.updatedAt } : {}),
          detailContentOverride: {
            ...original,
            presentation: valid,
            galleryImageUrls: gallery,
            itinerary: {
              version: 1,
              stops: decorations.filter((s) => stops.some((row) => row.name === s.name)),
            },
          },
        },
      );
      setBaseline(JSON.stringify({ presentation, decorations, gallery }));
      setMessage('线路介绍与详情展示已保存');
      onDone();
    } catch (error) {
      setFailed(true);
      setMessage(error instanceof ApiError ? error.message : '保存失败，请重试');
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  return (
    <section className="product-presentation-editor" aria-label="线路介绍与详情展示">
      <div className="product-presentation-editor__title">
        <h2>线路介绍与详情展示</h2>
        <span>当前小程序</span>
      </div>
      {!binding.id && !loading && (
        <p className="product-presentation-editor__error">
          请先在“小程序覆盖”中关联当前产品，再维护展示内容。
        </p>
      )}
      <fieldset disabled={busy || loading || !binding.id}>
        <div className="product-presentation-editor__fields">
          <label>
            行程天数
            <input
              type="number"
              min="1"
              max="365"
              value={presentation.days ?? ''}
              onChange={(e) => set('days', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="未填写时按套餐时长显示"
            />
          </label>
          <label>
            住宿晚数
            <input
              type="number"
              min="0"
              max="364"
              value={presentation.nights ?? ''}
              onChange={(e) => set('nights', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="当天往返填 0"
            />
          </label>
          <label>
            出发城市
            <input
              value={presentation.departureCity}
              maxLength={40}
              onChange={(e) => set('departureCity', e.target.value)}
              placeholder="例如：景洪"
            />
          </label>
          <label>
            住宿标准
            <input
              value={presentation.accommodation}
              maxLength={60}
              onChange={(e) => set('accommodation', e.target.value)}
              placeholder="例如：不含住宿 / 五星酒店"
            />
          </label>
          <label>
            成团方式
            <select
              value={presentation.groupLabel}
              onChange={(e) => set('groupLabel', e.target.value)}
            >
              <option value="">跟随所选套餐</option>
              {['自由行', '独立成团', '散客拼团', '私享小团'].map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </label>
          <label className="product-presentation-editor__wide">
            线路亮点
            <input
              value={presentation.highlights.join('，')}
              onChange={(e) => set('highlights', e.target.value.split(/[，,]/u))}
              placeholder="逗号分隔，例如：纯玩无购物，中文向导，含往返接送"
            />
          </label>
        </div>
        <div className="product-presentation-editor__subheading">
          <h3>头图轮播</h3>
          <small>从已上传产品图片中选择；未选择时使用产品图库</small>
        </div>
        <div className="product-presentation-editor__choices">
          {imageOptions.map((m) => (
            <label key={m.url}>
              <input
                type="checkbox"
                checked={gallery.includes(m.url)}
                onChange={(e) =>
                  setGallery(
                    e.target.checked ? [...gallery, m.url] : gallery.filter((url) => url !== m.url),
                  )
                }
              />
              {thumbnail(m.url) && <img src={thumbnail(m.url)} alt="" loading="lazy" />}
              {m.name}
            </label>
          ))}
        </div>
        <div className="product-presentation-editor__subheading">
          <h3>行程节点图文</h3>
          <a
            href={`/admin/products/${encodeURIComponent(String(product.id))}/itinerary?appId=${encodeURIComponent(appId)}`}
          >
            维护节点名称、说明与顺序
          </a>
        </div>
        <div className="product-presentation-editor__stops">
          {stops.map((s, index) => {
            const name = String(s.name),
              item = decorations.find((d) => d.name === name) ?? { tags: [], images: [] };
            return (
              <div key={String(s.id ?? name)} className="product-presentation-editor__stop">
                <b>
                  {String(index + 1).padStart(2, '0')} · {name}
                </b>
                <label>
                  节点标签
                  <input
                    aria-label={`${name}标签`}
                    value={(item.tags ?? []).join('，')}
                    onChange={(e) => updateStop(name, { tags: e.target.value.split(/[，,]/u) })}
                    placeholder="最多 4 个，逗号分隔"
                  />
                </label>
                <div className="product-presentation-editor__choices" aria-label={`${name}配图`}>
                  {imageOptions.map((m) => (
                    <label key={m.url}>
                      <input
                        type="checkbox"
                        checked={(item.images ?? []).some((i) => i.url === m.url)}
                        onChange={(e) =>
                          updateStop(name, {
                            images: e.target.checked
                              ? [...(item.images ?? []), { url: m.url, caption: name }]
                              : item.images.filter((i) => i.url !== m.url),
                          })
                        }
                      />
                      {thumbnail(m.url) && <img src={thumbnail(m.url)} alt="" loading="lazy" />}
                      {m.name}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="product-presentation-editor__subheading">
          <h3>线路介绍 · 图文编排</h3>
          <small>显示在行程安排下方，按内容块顺序展示</small>
        </div>
        <div className="product-presentation-editor__blocks">
          {presentation.introduction.map((block, index) => (
            <article key={block.id}>
              <div className="product-presentation-editor__block-head">
                <b>
                  {index + 1} ·{' '}
                  {block.type === 'heading' ? '标题' : block.type === 'paragraph' ? '正文' : '图片'}
                </b>
                <div>
                  <button
                    type="button"
                    aria-label={`上移第${index + 1}块`}
                    disabled={index === 0}
                    onClick={() => moveBlock(index, -1)}
                  >
                    上移
                  </button>
                  <button
                    type="button"
                    aria-label={`下移第${index + 1}块`}
                    disabled={index === presentation.introduction.length - 1}
                    onClick={() => moveBlock(index, 1)}
                  >
                    下移
                  </button>
                  <select
                    aria-label={`在第${index + 1}块后插入`}
                    value=""
                    onChange={(e) =>
                      addBlock(e.target.value as ProductIntroBlock['type'], index + 1)
                    }
                  >
                    <option value="">插入内容</option>
                    <option value="heading">标题</option>
                    <option value="paragraph">正文</option>
                    <option value="image">图片</option>
                  </select>
                  <button
                    type="button"
                    onClick={() =>
                      set(
                        'introduction',
                        presentation.introduction.filter((b) => b.id !== block.id),
                      )
                    }
                  >
                    移除
                  </button>
                </div>
              </div>
              {block.type === 'image' ? (
                <div className="product-presentation-editor__image-fields">
                  {block.url && thumbnail(block.url) && (
                    <img
                      className="product-presentation-editor__image-preview"
                      src={thumbnail(block.url)}
                      alt={block.caption || '已选图片'}
                      loading="lazy"
                    />
                  )}
                  <label>
                    图片
                    <select
                      value={block.url}
                      onChange={(e) => updateBlock(block.id, { url: e.target.value })}
                    >
                      <option value="">选择已上传图片</option>
                      {imageOptions.map((m) => (
                        <option key={m.url} value={m.url}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    图片说明
                    <input
                      value={block.caption}
                      maxLength={120}
                      onChange={(e) => updateBlock(block.id, { caption: e.target.value })}
                    />
                  </label>
                </div>
              ) : block.type === 'heading' ? (
                <input
                  aria-label={`第${index + 1}块标题`}
                  maxLength={100}
                  value={block.text}
                  onChange={(e) => updateBlock(block.id, { text: e.target.value })}
                />
              ) : (
                <textarea
                  aria-label={`第${index + 1}块正文`}
                  rows={3}
                  maxLength={4000}
                  value={block.text}
                  onChange={(e) => updateBlock(block.id, { text: e.target.value })}
                />
              )}
            </article>
          ))}
        </div>
        <div className="product-presentation-editor__add">
          <button type="button" onClick={() => addBlock('heading')}>
            ＋ 标题
          </button>
          <button type="button" onClick={() => addBlock('paragraph')}>
            ＋ 正文
          </button>
          <button type="button" onClick={() => addBlock('image')}>
            ＋ 图片
          </button>
        </div>
        <div className="product-presentation-editor__footer">
          <span role="status" className={failed ? 'product-presentation-editor__error' : ''}>
            {message || (dirty ? '有未保存的修改' : '')}
          </span>
          <button
            type="button"
            className="primary"
            disabled={!dirty || busy}
            onClick={() => void save()}
          >
            {busy ? '保存中…' : '保存线路介绍与展示'}
          </button>
        </div>
      </fieldset>
    </section>
  );
}
